import { useTreeHistoryStore } from "@components/tree/treeHistoryStore";
import { deleteObjectTree } from "./cascadeDelete";
import { type AutoEmptyTrashDays, db } from "./db";
import {
  GNOMUX_ROOT_ID,
  GNOMUX_TRASH_ID,
  type GnomuxFile,
  type GnomuxObject,
} from "./objects";

export type RestoreResult =
  | { restored: true }
  | { restored: false; reason: "parent_deleted" };

export async function moveToTrash(
  objectId: string,
  recordHistory = true,
): Promise<void> {
  let previousParent: string | undefined;

  await db.transaction("rw", [db.objects, db.recents], async () => {
    const object = await db.objects.get(objectId);
    if (!object) return;

    const file = object as GnomuxFile;
    previousParent = file.parent;

    await db.objects.update(objectId, {
      deletedAt: new Date(),
      originalParent: previousParent,
      parent: GNOMUX_TRASH_ID,
    });

    await db.recents.delete(objectId);
  });

  if (recordHistory && previousParent !== undefined) {
    useTreeHistoryStore.getState().push({
      kind: "delete",
      objectId,
      previousParent,
    });
  }
}

export async function restoreFromTrash(
  objectId: string,
  targetParent?: string,
  recordHistory = true,
): Promise<RestoreResult> {
  let restoredToParent: string | undefined;

  const result = await db.transaction("rw", [db.objects], async () => {
    const object = await db.objects.get(objectId);
    if (!object) return { restored: true } as const;

    const file = object as GnomuxFile & GnomuxObject;
    const originalParent = file.originalParent ?? GNOMUX_ROOT_ID;

    const restoreParent = targetParent ?? originalParent;

    if (restoreParent !== GNOMUX_ROOT_ID && !targetParent) {
      const parentObject = await db.objects.get(restoreParent);
      if (!parentObject || parentObject.deletedAt) {
        return { restored: false, reason: "parent_deleted" } as const;
      }
    }

    await db.objects.update(objectId, {
      deletedAt: undefined,
      originalParent: undefined,
      parent: restoreParent,
    });

    restoredToParent = restoreParent;
    return { restored: true } as const;
  });

  if (recordHistory && result.restored && restoredToParent !== undefined) {
    useTreeHistoryStore.getState().push({
      kind: "restore",
      objectId,
      restoredToParent,
    });
  }

  return result;
}

export async function permanentlyDelete(file: GnomuxFile): Promise<void> {
  await deleteObjectTree(file.id);
}

export async function autoEmptyTrash(days: AutoEmptyTrashDays): Promise<void> {
  if (days === 0) return;

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);

  const expiredItems = await db.objects
    .where("deletedAt")
    .below(cutoff)
    .toArray();

  for (const item of expiredItems) {
    await deleteObjectTree(item.id);
  }
}

export async function deleteErrorObject(objectId: string): Promise<void> {
  await deleteObjectTree(objectId);
}
